import RightPanelLayout from "@/components/layout/right-panel-layout";
import Sidebar from "@/components/layout/side-bar";
import SplashScreen from "@/components/layout/splash-screen";
import PanelDrawer from "@/components/layout/panel-drawer";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { usePanel } from "@/context/panel-context";
import { useAuth } from "react-oidc-context";
import { INITIAL_RESIZE_DIMENSIONS } from "@/lib/constants";
import { useEffect } from "react";
import { Outlet, useParams, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useSaveUserLogin from "@/service-hooks/use-save-user-login-service";
import { requestAuthFromOtherTabs, setupAuthRequestListener, broadcastAuthState, setupLogoutListener } from "@/lib/auth-sync";

export default function ProtectedRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { panelType } = usePanel();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const { mutate: saveLogin } = useSaveUserLogin((res, error) => {
    if (error) {
      console.error("Error saving user login:", error);
      auth.signoutRedirect();
    }
    if (res) {
      queryClient.setQueryData(["user"], res);
    }
  });

  const dimensions = INITIAL_RESIZE_DIMENSIONS;

  // Setup cross-tab logout listener
  useEffect(() => {
    const cleanup = setupLogoutListener(() => {
      // Logout this tab when another tab logs out
      auth.signoutRedirect();
    });

    return cleanup;
  }, [auth]);

  // Setup cross-tab auth sync listener
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    // Listen for auth requests from other tabs
    const cleanup = setupAuthRequestListener(() => auth.isAuthenticated);

    // Broadcast that this tab is authenticated
    broadcastAuthState(true);

    return cleanup;
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user && auth.user.access_token) {
      saveLogin();
    }
  }, [auth.isAuthenticated, auth.user?.access_token, saveLogin]);

  useEffect(() => {
    // Give auth a moment to check stored credentials before redirecting
    const checkAuth = async () => {
      // Wait for auth to finish loading
      if (auth.isLoading) return;

      // If there's an active navigator (redirect in progress), don't interfere
      if (auth.activeNavigator) return;

      // If not authenticated after loading completes, try to get auth from other tabs
      if (!auth.isAuthenticated) {
        // Try to sync auth state from other tabs
        const gotAuth = await requestAuthFromOtherTabs();

        if (gotAuth) {
          // Force reload to pick up the synced auth state
          window.location.reload();
          return;
        }

        // No auth found in any tab - redirect to login
        sessionStorage.setItem("returnUrl", location.pathname + location.search);
        navigate("/login", { replace: true });
      }
    };

    // Small delay to allow auth state to be read from sessionStorage
    const timeoutId = setTimeout(checkAuth, 100);
    return () => clearTimeout(timeoutId);
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    auth.activeNavigator,
    location.pathname,
    location.search,
    navigate,
  ]);

  if (auth.isLoading) {
    return <SplashScreen />;
  }

  if (!auth.isAuthenticated) {
    return <SplashScreen />;
  }

  return (
    <div className="h-screen w-screen flex bg-surface">
      <ResizablePanelGroup direction="horizontal">
        <ResizablePanel minSize={dimensions.left}>
          <div className="h-screen flex">
            <Sidebar />
            <Outlet />
          </div>
        </ResizablePanel>
        {id &&
          panelType &&
          !["UNIFIED_MODEL", "METADATA_VIEWER", "MARKDOWN_RENDER", "MEDIA_PREVIEW"].includes(panelType) && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel
                key={JSON.stringify(dimensions)}
                minSize={dimensions.right}
              >
                <RightPanelLayout />
              </ResizablePanel>
            </>
          )}
      </ResizablePanelGroup>

      {/* Drawer overlay - rendered outside the layout structure */}
      <PanelDrawer />
    </div>
  );
}
